import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {
    FormattedMessage,
    injectIntl,
    intlShape
} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import {login, register} from '../../lib/auth-api.js';
import {
    describeAuthError,
    messages as authErrorMessages
} from '../../lib/auth-error-messages.js';
import {
    loginFailure,
    loginRequest,
    loginSuccess
} from '../../reducers/auth';
import Box from '../box/box.jsx';
import styles from './login-popup.css';

// Kept in step with the authentication service's own limits.
const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

const messages = Object.assign({}, authErrorMessages, {
    passwordHint: authErrorMessages.passwordHint,
    titleLogin: {
        id: 'gui.login.title',
        defaultMessage: '登录',
        description: 'Title of the login dialog'
    },
    titleRegister: {
        id: 'gui.login.registerTitle',
        defaultMessage: '注册账号',
        description: 'Title of the registration dialog'
    }
});

class LoginPopup extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            account: '',
            displayName: '',
            error: null,
            mode: 'login',
            password: ''
        };
        bindAll(this, [
            'handleAccountChange',
            'handleDisplayNameChange',
            'handlePasswordChange',
            'handleSubmit',
            'handleClose',
            'handleSwitchMode'
        ]);
    }

    handleAccountChange (event) {
        this.setState({
            account: event.target.value,
            error: null
        });
    }

    handleDisplayNameChange (event) {
        this.setState({
            displayName: event.target.value,
            error: null
        });
    }

    handlePasswordChange (event) {
        this.setState({
            password: event.target.value,
            error: null
        });
    }

    handleSubmit (event) {
        event.preventDefault();
        const account = this.state.account.trim();
        const password = this.state.password;
        const isRegistering = this.state.mode === 'register';

        if (!account || !password) {
            this.setState({error: this.props.intl.formatMessage(messages.empty)});
            return;
        }
        if (isRegistering && !this.state.displayName.trim()) {
            this.setState({error: this.props.intl.formatMessage(messages.registerEmptyDisplayName)});
            return;
        }
        // The server enforces 10-128 characters. Checking here as well means the
        // user always sees a clear Chinese explanation: the browser's built-in
        // minLength bubble is easy to miss and its wording depends on the
        // browser language.
        if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
            this.setState({error: this.props.intl.formatMessage(messages.invalidPassword)});
            return;
        }

        this.props.onLoginRequest();
        const authenticate = isRegistering ? register({
            account: account,
            displayName: this.state.displayName.trim(),
            password: password
        }) : login({account: account, password: password});

        authenticate
            .then(user => {
                this.props.onLoginSuccess(user);
                this.setState({
                    account: '',
                    displayName: '',
                    error: null,
                    password: ''
                });
                this.props.onRequestClose();
            })
            .catch(error => {
                this.props.onLoginFailure(error && error.code ? error.code : 'AUTH_REQUEST_FAILED');
                this.setState({
                    error: this.getErrorMessage(error),
                    password: ''
                });
            });
    }

    getErrorMessage (error) {
        const descriptor = describeAuthError(error);
        return this.props.intl.formatMessage(descriptor, descriptor.values);
    }

    handleClose () {
        this.setState({
            account: '',
            displayName: '',
            error: null,
            mode: 'login',
            password: ''
        });
        this.props.onRequestClose();
    }

    handleSwitchMode () {
        this.setState(previousState => ({
            error: null,
            mode: previousState.mode === 'login' ? 'register' : 'login',
            password: ''
        }));
    }

    render () {
        const isRegistering = this.state.mode === 'register';
        const title = isRegistering ? messages.titleRegister : messages.titleLogin;
        return (
            <Modal
                className={styles.modalContent}
                contentLabel={this.props.intl.formatMessage(title)}
                id="loginPopup"
                onRequestClose={this.handleClose}
            >
                <Box
                    className={styles.body}
                    direction="column"
                >
                    <h2 className={styles.title}>
                        <FormattedMessage {...title} />
                    </h2>
                    <form onSubmit={this.handleSubmit}>
                        {isRegistering ? (
                            <React.Fragment>
                                <label
                                    className={styles.label}
                                    htmlFor="register-display-name"
                                >
                                    <FormattedMessage
                                        defaultMessage="显示名称"
                                        description="Label for registration display name"
                                        id="gui.login.displayName"
                                    />
                                </label>
                                <input
                                    autoComplete="name"
                                    className={styles.input}
                                    disabled={this.props.isSubmitting}
                                    id="register-display-name"
                                    maxLength="64"
                                    type="text"
                                    value={this.state.displayName}
                                    onChange={this.handleDisplayNameChange}
                                />
                            </React.Fragment>
                        ) : null}

                        <label
                            className={styles.label}
                            htmlFor="login-account"
                        >
                            <FormattedMessage
                                defaultMessage="账号"
                                description="Label for login account"
                                id="gui.login.account"
                            />
                        </label>
                        <input
                            autoComplete="username"
                            className={styles.input}
                            disabled={this.props.isSubmitting}
                            id="login-account"
                            maxLength="32"
                            type="text"
                            value={this.state.account}
                            onChange={this.handleAccountChange}
                        />

                        <label
                            className={styles.label}
                            htmlFor="login-password"
                        >
                            <FormattedMessage
                                defaultMessage="密码"
                                description="Label for login password"
                                id="gui.login.password"
                            />
                        </label>
                        <input
                            autoComplete={isRegistering ? 'new-password' : 'current-password'}
                            className={styles.input}
                            disabled={this.props.isSubmitting}
                            id="login-password"
                            maxLength={MAX_PASSWORD_LENGTH}
                            type="password"
                            value={this.state.password}
                            onChange={this.handlePasswordChange}
                        />
                        {isRegistering ? (
                            <p className={styles.hint}>
                                <FormattedMessage
                                    defaultMessage="至少 {min} 位字符。"
                                    description="Hint for the minimum password length"
                                    id="gui.login.passwordHint"
                                    values={{min: MIN_PASSWORD_LENGTH}}
                                />
                            </p>
                        ) : null}

                        {this.state.error ? (
                            <p
                                className={styles.error}
                                role="alert"
                            >{this.state.error}</p>
                        ) : null}

                        <button
                            className={styles.submitButton}
                            disabled={this.props.isSubmitting}
                            type="submit"
                        >
                            {this.props.isSubmitting ? (
                                <FormattedMessage
                                    defaultMessage="处理中…"
                                    description="Text shown while an authentication request is pending"
                                    id="gui.login.submitting"
                                />
                            ) : (isRegistering ? (
                                <FormattedMessage
                                    defaultMessage="注册并登录"
                                    description="Submit the registration form"
                                    id="gui.login.registerSubmit"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="登录"
                                    description="Submit the login form"
                                    id="gui.login.submit"
                                />
                            ))}
                        </button>

                        <button
                            className={styles.switchModeButton}
                            disabled={this.props.isSubmitting}
                            type="button"
                            onClick={this.handleSwitchMode}
                        >
                            {isRegistering ? (
                                <FormattedMessage
                                    defaultMessage="已有账号？去登录"
                                    description="Switch from registration to login"
                                    id="gui.login.switchToLogin"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="没有账号？立即注册"
                                    description="Switch from login to registration"
                                    id="gui.login.switchToRegister"
                                />
                            )}
                        </button>
                    </form>
                </Box>
            </Modal>
        );
    }
}

LoginPopup.propTypes = {
    intl: intlShape.isRequired,
    isSubmitting: PropTypes.bool,
    onLoginFailure: PropTypes.func.isRequired,
    onLoginRequest: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    isSubmitting: state.scratchGui.auth.status === 'authenticating'
});

const mapDispatchToProps = dispatch => ({
    onLoginRequest: () => dispatch(loginRequest()),
    onLoginSuccess: user => dispatch(loginSuccess(user)),
    onLoginFailure: error => dispatch(loginFailure(error))
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(LoginPopup));

// Exported unwrapped so tests can exercise the error-message mapping without a
// Redux store.
export {LoginPopup};
